import { useRef, useState } from "react";
import { Camera, Trash2, X, Loader2, ImagePlus } from "lucide-react";
import api from "../lib/api";
import ConfirmDialog from "./ConfirmDialog";

const MAX_PHOTOS = 8;
const MAX_DIMENSION = 1400; // resize to max 1400px
const JPEG_QUALITY = 0.75;

// Compress an image File to a JPEG data URL
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          const scale = MAX_DIMENSION / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function OrderPhotos({ order, onChange }) {
  const photos = order.photos || [];
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { url, index }
  const [confirmDel, setConfirmDel] = useState(null);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setErr("");
    setUploading(true);
    try {
      let count = photos.length;
      for (const file of files) {
        if (count >= MAX_PHOTOS) {
          setErr(`Máximo de ${MAX_PHOTOS} fotos por OS`);
          break;
        }
        if (!file.type.startsWith("image/")) continue;
        try {
          const dataUrl = await compressImage(file);
          await api.post(`/orders/${order.id}/photos`, { dataUrl });
          count += 1;
        } catch (e) {
          setErr(e.response?.data?.detail || "Erro ao enviar foto.");
        }
      }
      onChange && (await onChange());
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const del = async (index) => {
    try {
      await api.delete(`/orders/${order.id}/photos/${index}`);
      onChange && (await onChange());
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao excluir foto.");
    }
  };

  return (
    <div className="space-y-2" data-testid="order-photos">
      <div className="flex items-center justify-between">
        <h4 className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">
          Fotos do aparelho ({photos.length}/{MAX_PHOTOS})
        </h4>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
            className="hidden"
            data-testid="photos-input"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || photos.length >= MAX_PHOTOS}
            data-testid="photos-add"
            className="h-8 px-3 rounded-md text-[11px] font-semibold bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
            Adicionar
          </button>
        </div>
      </div>

      {err && (
        <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">{err}</div>
      )}

      {photos.length === 0 ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full py-6 rounded-lg border-2 border-dashed border-slate-200 text-center text-xs text-slate-400 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50/30 transition-all inline-flex flex-col items-center gap-2"
          data-testid="photos-empty"
        >
          <Camera className="w-6 h-6" />
          Nenhuma foto ainda — clique para adicionar (usa câmera do celular ou arquivo)
        </button>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((url, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50 group">
              <img
                src={url}
                alt={`Foto ${i + 1}`}
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => setPreview({ url, index: i })}
                data-testid={`photo-${i}`}
              />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setConfirmDel(i); }}
                data-testid={`photo-delete-${i}`}
                className="absolute top-1 right-1 p-1 rounded bg-white/90 text-rose-600 hover:bg-rose-600 hover:text-white opacity-0 group-hover:opacity-100 transition"
                title="Excluir foto"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Fullscreen preview */}
      {preview && (
        <div
          className="fixed inset-0 z-[70] bg-slate-900/85 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
          data-testid="photo-preview"
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
            onClick={() => setPreview(null)}
          >
            <X className="w-5 h-5" />
          </button>
          <img src={preview.url} alt="Preview" className="max-w-full max-h-full rounded-lg shadow-2xl" />
        </div>
      )}

      <ConfirmDialog
        open={confirmDel !== null}
        title="Excluir foto"
        message="Deseja excluir esta foto? Essa ação não pode ser desfeita."
        confirmLabel="Excluir"
        onCancel={() => setConfirmDel(null)}
        onConfirm={async () => { const i = confirmDel; setConfirmDel(null); await del(i); }}
      />
    </div>
  );
}
